<template>
  <div class="module-container documents-container">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <div class="d-flex flex-row justify-content-start align-items-center">
        <div
          class="main-image-container bg-purple rounded d-flex justify-content-center align-items-center"
        >
          <img
            height="25"
            width="25"
            :src="require('../../assets/icons/clock.svg')"
          />
        </div>
        <h3 class="mb-0 ml-3">Appointment Requests</h3>
      </div>
    </div>

    <div class="filters-container" ref="filtersContainer">
      <div
        class="d-flex flex-row justify-content-between align-items-center bg-light-gray min-width-fit-content"
      >
        <div class="d-flex flex-wrap align-items-center">
          <b-form-input
            class="auth-input search-input border-color-light-gray"
            v-model="filter.searchPhrase"
            placeholder="Search Patient..."
            :readonly="searchingProvider || searchingService"
          />
          <span class="ml-4 mr-3" style="font-size: 14px;"
            >Active filters:</span
          >

          <div
            class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
            v-if="filter.searchPhrase.length >= 3"
          >
            <div class="d-flex justify-content-between align-items-center">
              <span class="mr-3"
                >Search Bar:
                <span class="color-filter">{{
                  filter.searchPhrase
                }}</span></span
              >
              <i
                class="fa fa-times text-danger hover-pointer hover-text-almost-black"
                @click="filter.searchPhrase = ''"
              ></i>
            </div>
          </div>

          <div
            class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
            v-for="(p, pIndex) in filter.provider"
            :key="p.value"
          >
            <div class="d-flex justify-content-between align-items-center">
              <span class="mr-3"
                >Provider: <span class="color-filter">{{ p.label }}</span></span
              >
              <i
                class="fa fa-times text-danger hover-pointer hover-text-almost-black"
                @click="removeProviderFormFilter(pIndex)"
              ></i>
            </div>
          </div>
          <div
            class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
            v-for="(s, sIndex) in filter.service"
            :key="s.value"
          >
            <div class="d-flex justify-content-between align-items-center">
              <span class="mr-3"
                >Service:
                <span class="color-filter">{{
                  s.label.length > 50
                    ? s.label.substr(0, 50).concat(`...`)
                    : s.label
                }}</span></span
              >
              <i
                class="fa fa-times text-danger hover-pointer hover-text-almost-black"
                @click="removeServiceFormFilter(sIndex)"
              ></i>
            </div>
          </div>
          <div
            v-if="filter.startDateTime || filter.endDateTime"
            class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          >
            <div class="d-flex justify-content-between align-items-center">
              <span class="mr-3"
                >Date Time Range:
                <span class="color-filter"
                  >{{
                    filter.startDateTime || filter.endDateTime
                      ? `(${
                          filter.startDateTime
                            ? "From: " + formatDateOnly(filter.startDateTime)
                            : ""
                        }${
                          filter.startDateTime && filter.endDateTime
                            ? " - "
                            : ""
                        }${
                          filter.endDateTime
                            ? "To: " + formatDateOnly(filter.endDateTime)
                            : ""
                        })`
                      : ""
                  }}
                </span></span
              >
              <i
                class="fa fa-times text-danger hover-pointer hover-text-almost-black"
                @click="removeDateFilter()"
              ></i>
            </div>
          </div>
          <span
            v-show="hasFilters"
            class="clear-all-filters text-danger ml-2"
            style="font-size: 14px;"
            @click="clearAllFilters"
            >Clear all filters</span
          >
        </div>
        <div class="d-flex align-self-stretch align-items-center">
          <div class="align-self-stretch" style="padding: 9px 0px;">
            <div class="vertical-separator border-med-light-gray"></div>
          </div>
          <b-dropdown
            id="dropdown-form"
            text="Filters"
            ref="filterDropdown"
            class="container-main bg-light-gray"
            toggle-class="d-flex flex-row justify-content-between align-items-lg-center bg-transparent border-0 color-custom-dark-grey dropdown-menu-label"
            menu-class="container-filter-main-menu"
            toggle-tag="div"
            boundary="window"
            right
            @hide="onFilterHide"
          >
            <template #button-content>
              <div class="d-flex align-items-center mr-2">
                <i
                  class="fas fa-filter mr-2"
                  :class="hasFilters ? 'text-primary' : ''"
                  style="font-size: 15px;"
                  aria-hidden="true"
                ></i>
                <span style="font-size: 14px;">Add Filter</span>
              </div>
            </template>
            <b-dropdown-form class="px-2" style="max-width:750px">
              <b-form-group label="Filter by:">
                <b-form-select
                  v-model="selectedFilter"
                  :options="filterOptions"
                />
              </b-form-group>

              <multiple-select-filter
                v-if="selectedFilter == 'provider'"
                v-model="filter.provider"
                :label="'Provider'"
                :options="providerOptions"
                :width="filterWidth"
                :showSearch="true"
                :async="true"
                :searching="searchingProvider"
                @on-search="fetchProviderOptions"
                @on-text-selection="updateTextSelection"
                :key="'provider'"
                ref="providerFilter"
              />

              <multiple-select-filter
                v-if="selectedFilter == 'service'"
                v-model="filter.service"
                :label="'Service'"
                :options="serviceOptions"
                :width="filterWidth"
                :showSearch="true"
                :async="true"
                :searching="searchingService"
                @on-search="fetchServiceOptions"
                @on-text-selection="updateTextSelection"
                :key="'service'"
                ref="serviceFilter"
              />

              <date-range-filter
                v-if="selectedFilter == 'dateTimeRange'"
                v-model="filterEffectiveDateRange"
                :filterWidth="filterWidth"
                label="Start Date Time"
                :key="'dateTimeRange'"
              />
            </b-dropdown-form>
          </b-dropdown>
        </div>
      </div>
    </div>

    <div class="mt-3 mb-0 w-100 d-flex flex-row justify-content-end">
      <div class="d-flex flex-row justify-content-end align-items-right">
        <top-paginator
          :total="listTotal"
          :page="filter.page"
          :perPage="filter.pageSize"
          @change-page="changePage"
        />
      </div>
    </div>

    <div
      :style="
        `--document-list-filters-container-height:calc(${filtersContainerHeight}px);`
      "
    >
      <grid
        ref="grid"
        :columns="columns"
        :data="items"
        :store="gridConfig.store"
        :selectionMode="gridConfig.selectionMode"
        :columnLines="gridConfig.columnLines"
        :groupFeature="false"
        :autoHeight="false"
        :searchFeature="true"
        :stripeFeature="false"
        @storesort="listeners.storeSort"
        @renderrows="hookDropdownButtons"
        @renderrow="hookDropdownButtons"
      />

      <appointment-request-view-modal
        v-if="appointmentRequestViewModal.show"
        v-model="appointmentRequestViewModal.show"
        @onModify="onAddOrEditAppointmentRequests"
        :appointmentRequestId="appointmentRequestViewModal.id"
        :appointmentRequestfilters="appointmentRequestViewModal.filters"
      />
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import Grid from "../../third_party/grid/Grid.vue";
import TopPaginator from "../controls/TopPaginator.vue";
import AppointmentRequestViewModal from "./AppointmentRequestViewModal.vue";
import AppointmentRequestServer from "../../server/appointmentRequest";
import { calculateDurationInMinutes } from "../../helpers/date";
import userServer from "../../server/user";
import serviceServer from "../../server/service";

export default {
  name: "AppointmentRequestIndex",
  components: {
    Grid,
    TopPaginator,
    AppointmentRequestViewModal
  },
  props: ["showing"],
  data() {
    const self = this;
    return {
      list: [],
      listTotal: 0,
      appointmentStatuses: [
        {
          value: "Pending",
          label: "Pending"
        },
        {
          value: "Approved",
          label: "Approved"
        },
        {
          value: "Denied",
          label: "Denied"
        }
      ],
      filterWidth: "250px",
      filterPatientWidth: "350px",
      selectedFilter: "provider",
      filterOptions: [
        {
          value: "provider",
          text: "Provider"
        },
        {
          value: "service",
          text: "Service"
        },
        {
          value: "dateTimeRange",
          text: "Start Date Time"
        }
      ],
      filter: {
        searchPhrase: "",
        page: 0,
        pageSize: 50,
        provider: [],
        service: [],
        startDateTime: null,
        endDateTime: null,
        orderBy: "dateTimeDuration",
        orderByDescending: true
      },
      gridConfig: {
        minHeight: "20em",
        selectionMode: {
          row: false,
          checkbox: false,
          rowCheckboxSelection: false,
          showCheckAll: false
        },
        columnLines: false,
        store: {
          // Default sorters
          sorters: [{ field: "dateTimeDuration", ascending: false }]
        }
      },
      showResults: false,
      showAddFileEditor: false,
      appointmentRequestViewModal: {
        show: false,
        id: null,
        filters: null
      },
      users: [],
      providerOptions: [],
      serviceOptions: [],
      lastTimeoutProviderOptions: null,
      lastTimeoutServiceOptions: null,
      searchingProvider: false,
      searchingService: false,
      filtersContainerHeight: 0,
      filterTextSelection: null,
      listeners: {
        storeSort: sortData => {
          if (sortData.sorters.length == 1) {
            const sorter = sortData.sorters[0];
            const ascending = sorter.ascending;
            const field = sorter.field;
            self.filter.orderBy = field;
            self.filter.orderByDescending = !ascending;
            self.changePage(0);
          }
        }
      }
    };
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    async fetchInitialData() {
      this.searchAppointmentRequest(false);
      this.fetchProviderOptions("");
      this.fetchServiceOptions();
    },
    searchAppointmentRequest(delayed = true) {
      if (delayed) {
        if (this.lastTimeout) {
          clearTimeout(this.lastTimeout);
          this.lastTimeout = null;
        }
        this.lastTimeout = setTimeout(async () => {
          this.fetchAppointmentRequest();
        }, 1000);
      } else this.fetchAppointmentRequest();

      this.showResults = true;
    },
    async fetchAppointmentRequest() {
      const result = await AppointmentRequestServer.query({
        searchPhrase:
          this.filter.searchPhrase.length >= 3 ? this.filter.searchPhrase : "",
        page: this.filter.page,
        pageSize: this.filter.pageSize,
        provider: this.filter.provider.map(x => x.value),
        service: this.filter.service.map(x => x.value),
        startDateTime: this.formatStartDateSql(this.filter.startDateTime),
        endDateTime: this.formatStartDateSql(this.filter.endDateTime),
        orderBy: this.filter.orderBy,
        orderByDescending: this.filter.orderByDescending
      });
      this.list = result.appointmentRequests;
      this.listTotal = result.count;
    },
    scrollGridToTop() {
      const grid = document.querySelector(".documents-container .b-grid");
      grid.scrollTop = 0;
    },
    async fetchProviderOptions(search) {
      this.searchingProvider = true;
      const users = await userServer.findAllProviders({
        SearchPhrase: search,
        Page: 0,
        PageSize: 100
      });
      this.providerOptions = users.providers.map(user => ({
        value: user.id,
        label: this.formatUser(user),
        data: user
      }));
      this.searchingProvider = false;
    },
    async fetchServiceOptions(search = "") {
      this.searchingService = true;
      const services = await serviceServer.query(
        {
          SearchPhrase: search
        },
        {
          "Hide-Loading": true
        }
      );
      this.serviceOptions = services.map(service => ({
        value: service.id,
        label: service.name,
        data: service
      }));
      this.searchingService = false;
    },
    onFilterHide(event) {
      let ref = null;
      if (this.selectedFilter == "provider") {
        ref = this.$refs.providerFilter;
      }
      if (this.selectedFilter == "service") {
        ref = this.$refs.serviceFilter;
      }
      if (this.selectedFilter == "dateTimeRange") {
        ref = this.$refs.dateTimeRange;
      }
      if (this.filterTextSelection) {
        if (this.filterTextSelection) event.preventDefault();
        ref.highlightText();
        this.filterTextSelection = null;
      }
    },
    changePage(page) {
      this.filter.page = page;
      this.fetchAppointmentRequest();
      this.scrollGridToTop();
    },
    updatePopper() {
      this.$refs.filterDropdown.updatePopper();
    },
    computeHeights() {
      setTimeout(() => {
        const filtersContainerHeight = this.$refs.filtersContainer.clientHeight;
        this.filtersContainerHeight = filtersContainerHeight;
      }, 0);
    },
    updateTextSelection(textSelection) {
      this.filterTextSelection = textSelection;
    },
    onAddOrEditAppointmentRequests() {
      this.appointmentRequestViewModal = {
        show: false,
        id: null,
        filters: null
      };
      this.$emit("onAddOrEditAppointmentRequests");
      this.fetchInitialData();
    },
    clearAllFilters() {
      this.filter.searchPhrase = "";
      this.filter.provider = [];
      this.filter.service = [];
      this.filter.startDateTime = null;
      this.filter.endDateTime = null;
    },
    removeProviderFormFilter(index) {
      const newValue = [...this.filter.provider];
      newValue.splice(index, 1);
      this.filter.provider = newValue;
    },
    removeServiceFormFilter(index) {
      const newValue = [...this.filter.service];
      newValue.splice(index, 1);
      this.filter.service = newValue;
    },
    removeDateFilter() {
      this.filter.startDateTime = null;
      this.filter.endDateTime = null;
    }
  },
  computed: {
    items() {
      return this.list.map(item => ({
        ...item
      }));
    },
    columns() {
      const self = this;
      return [
        {
          text: "Type",
          field: "type",
          type: "template",
          width: 130,
          editor: false,
          template: ({ record }) => {
            let type = "New Patient";
            let className = "warning";
            let padding = 4;
            if (record.data.type == "ExistingPatient") {
              type = "Existing Patient";
              className = "success";
              padding = 3;
            }
            return `<span class="badge-v2 py-2 px-${padding} badge-${className} badge-fixed-size">${type}</span>`;
          }
        },
        {
          text: "Patient Name",
          field: "patientName",
          type: "template",
          flex: 1,
          editor: false,
          template: data =>
            (data.record.data.firstName ?? "") +
            " " +
            (data.record.data.lastName ?? ""),
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "MRN#",
          field: "mrn",
          type: "template",
          width: 100,
          editor: false,
          template: data =>
            data.record.data.episodeOfCare
              ? data.record.data.episodeOfCare.mrnEocn
              : ""
        },
        {
          text: "Provider",
          field: "provider",
          type: "template",
          flex: 1,
          editor: false,
          template: data => this.formatUser(data.record.data.provider),
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Date, Time and Duration",
          field: "dateTimeDuration",
          type: "template",
          width: 250,
          editor: false,
          template: data =>
            this.formatDate(data.record.data.startDateTime) +
            " (" +
            calculateDurationInMinutes(
              data.record.data.startDateTime,
              data.record.data.endDateTime
            ) +
            " min)",
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Service",
          field: "service",
          type: "template",
          width: 250,
          editor: false,
          template: data => {
            if (data.record.data.service.billingCodes.length > 0) {
              return (
                data.record.data.service.billingCodes[0].cptCode +
                " - " +
                data.record.data.service.name
              );
            } else {
              return data.record.data.service.name;
            }
          },
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "",
          type: "action",
          cellCls: "pointer-cell",
          align: "right",
          width: 80,
          actions: [
            {
              tooltip: "View",
              renderer: () =>
                `<i class="fa fa-search-plus color-custom-dark-grey hover-text-primary b-action-item  grid-action-icon"></i>`,
              onClick: ({ record }) => {
                const filtersAppointmentRequest = {
                  searchPhrase:
                    this.filter.searchPhrase.length >= 3
                      ? this.filter.searchPhrase
                      : "",
                  page: this.filter.page,
                  pageSize: this.filter.pageSize,
                  provider: this.filter.provider.map(x => x.value),
                  service: this.filter.service.map(x => x.value),
                  startDateTime: this.formatStartDateSql(
                    this.filter.startDateTime
                  ),
                  endDateTime: this.formatStartDateSql(this.filter.endDateTime),
                  orderBy: this.filter.orderBy,
                  orderByDescending: this.filter.orderByDescending
                };
                this.appointmentRequestViewModal = {
                  show: true,
                  id: record.data.id,
                  filters: filtersAppointmentRequest
                };
              }
            },
            {
              tooltip: "More",
              renderer: ({ record }) => {
                const actions = [];
                actions.push({
                  content: "Delete",
                  callback: () => {
                    this.$dialog
                      .alert(
                        {
                          title: "Are you sure you want to delete this record?"
                        },
                        {
                          view: "confirm-delete"
                        }
                      )
                      .then(async () => {
                        let result = null;
                        result = await AppointmentRequestServer.delete(
                          record.data.id
                        );
                        if (result.errorCode) {
                          this.$dialog.alert("Error: " + result.message);
                        } else {
                          this.fetchInitialData();
                          this.showAlertSuccess();
                        }
                      });
                  }
                });

                return this.createDropdownButton({
                  items: actions
                });
              }
            }
          ]
        }
      ];
    },
    hasFilters() {
      return (
        this.filter.searchPhrase.length > 0 ||
        this.filter.provider.length > 0 ||
        this.filter.service.length > 0 ||
        this.filter.startDateTime ||
        this.filter.endDateTime
      );
    },
    pageSize: {
      get() {
        return this.filter.pageSize.toString();
      },
      set(newValue) {
        this.filter.pageSize = parseInt(newValue);
        this.changePage(0);
      }
    },
    filterEffectiveDateRange: {
      get() {
        return {
          dateFrom: this.filter.startDateTime,
          dateTo: this.filter.endDateTime
        };
      },
      set(newValue) {
        this.filter.startDateTime = newValue.dateFrom;
        this.filter.endDateTime = newValue.dateTo;
      }
    }
  },
  watch: {
    showing: function(newValue) {
      if (newValue) {
        this.fetchInitialData();
        this.computeHeights();
      }
    },
    "filter.searchPhrase": function() {
      this.filter.page = 0;
      if (
        this.filter.searchPhrase.length == 0 ||
        this.filter.searchPhrase.length >= 3
      ) {
        this.searchAppointmentRequest();
      }
    },
    "filter.provider": function() {
      this.filter.page = 0;
      this.searchAppointmentRequest();
      this.computeHeights();
    },
    "filter.service": function() {
      this.filter.page = 0;
      this.searchAppointmentRequest();
      this.computeHeights();
    },
    "filter.startDateTime": function() {
      this.filter.page = 0;
      this.searchAppointmentRequest();
      this.computeHeights();
    },
    "filter.endDateTime": function() {
      this.filter.page = 0;
      this.searchAppointmentRequest();
      this.computeHeights();
    },
    selectedFilter: function() {
      this.$refs.filterDropdown.updatePopper();
    }
  }
};
</script>

<style scoped>
.container-main {
  height: 45px;
}
.b-dropdown-form:focus {
  outline: none !important;
}

.filter-value {
  min-width: 100px;
  height: 29px;
  color: #a6a6a6;
  font-size: 14px;
  padding-left: 10px;
}
.filter-value-delete {
  position: absolute;
  top: -8px;
  right: -8px;
}
.filter-value-delete:hover {
  cursor: pointer;
}
.clear-all-filters:hover {
  cursor: pointer;
}
.filter-title {
  font-size: 16px;
}
.admission-data {
  font-size: 13px;
}

.filters-container >>> .container-filter-main-menu {
  min-width: 250px;
  left: -17px;
}

.documents-container >>> .b-grid-header .b-checkbox {
  margin-left: -7px;
}
.documents-container >>> .file-result-row .b-checkbox-selection div {
  display: none;
}
.documents-container >>> .b-grid-container .b-container {
  max-height: calc(
    100vh - var(--document-list-filters-container-height) - 240px
  );
  min-height: calc(
    100vh - var(--document-list-filters-container-height) - 240px
  ) !important;
}
.vertical-separator {
  width: 1px;
  border-left: 1px solid;
  height: 100%;
}
.color-filter {
  color: #909db8;
}

.documents-container >>> .b-action-cell .b-action-ct {
  display: flex;
  align-items: center;
  gap: 5px;
}

.search-input {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  width: 320px;
}
</style>
